import { yupResolver } from '@hookform/resolvers/yup';
import { Cancel, Send } from '@mui/icons-material';
import BorderColorOutlinedIcon from '@mui/icons-material/BorderColorOutlined';
import CloseIcon from '@mui/icons-material/Close';
import InsertLinkOutlinedIcon from '@mui/icons-material/InsertLinkOutlined';
import EmailTemplateIcon from '@mui/icons-material/ViewCompactOutlined';
import {
  Stack,
  Grid,
  Select,
  MenuItem,
  Box,
  styled,
  InputLabel,
  Chip,
  IconButton,
  DialogContent,
} from '@mui/material';
import { isEmpty } from 'lodash-es';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { useForm, Controller, FormProvider } from 'react-hook-form';
import { object, string, array } from 'yup';

import WsColors from '../../WsColors';
import { ControlledTextInput } from '../ControlledTextInput';
import EmailFooter from '../EmailFooter';
import EmailInputField from '../EmailInputField';
import EmailTextBorder from '../EmailTextBorder';
import { Item } from '../Item';
import { Layout } from '../Layout';
import { WsButton } from '../WsButton';
import WsDialog, { WsDialogSize } from '../WsDialog/WsDialog';
import { WsEditor, useWsEditor } from '../WsEditor';
import WsTypography, { WsTypoColors, WsTypoVariants } from '../WsTypography/WsTypography';

import EmailPreview from './EmailBodyPreview';

const validationSchema = {
  cc: array().of(string().email('Please enter valid email addresses')),
  bcc: array().of(string().email('Please enter valid email addresses')),
  from: string().required('This field is required').email('Please enter a valid email address'),
  subject: string().required('Please enter a subject line'),
  body: string().required('The body cannot be empty'),
  to: array().min(1, 'Please enter at least one email address'),
};

export const AttachmentIcon = styled(InsertLinkOutlinedIcon)(() => ({
  color: WsColors.TheBlues500,
  transform: 'rotateZ(-45deg)',
}));

export const FileInput = styled(InputLabel)(() => ({
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  justifyContent: 'space-between',
}));

const SubjectInput = (disable?: boolean) => (
  <Grid xs={10}>
    <ControlledTextInput
      disabled={disable ?? false}
      name="subject"
      type="text"
      fullWidth
      variant="standard"
      inputProps={{
        style: {
          padding: 0,
        },
      }}
      InputProps={{
        disableUnderline: true,
      }}
    />
  </Grid>
);

const FormLabel = (label: string) => (
  <Grid xs={2} item>
    <Stack direction="row" justifyContent={'space-between'} pr={2}>
      <WsTypography bold={true} color={WsTypoColors.TERTIARY} variant={WsTypoVariants.BODY1}>
        {label}
      </WsTypography>
      <WsTypography bold={true} color={WsTypoColors.TERTIARY} variant={WsTypoVariants.BODY1}>
        :
      </WsTypography>
    </Stack>
  </Grid>
);

const JobInput = (jobTitle: string | null, jobCode: string | null) => (
  <Grid item xs={7}>
    <Stack direction="row" alignItems="center" spacing={2}>
      <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.PRIMARY}>
        {jobTitle ?? ''}
      </WsTypography>
      <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.TERTIARY}>
        {jobCode ?? ''}
      </WsTypography>
    </Stack>
  </Grid>
);

const ReceiverInput = (name: 'to' | 'cc' | 'bcc', disableEdit?: boolean) => (
  <Grid xs={7} item>
    <EmailInputField
      disable={disableEdit ?? false}
      name={name}
      placeholder={''}
      options={[]}
      freeSolo={true}
      multiple={true}
    />
  </Grid>
);
export interface IBulkEmailFormProps {
  body?: string | null;
  jobTitle: string | null;
  jobCode: string | null;
  template?: string | null;
  subjectTemplate?: string | null;
  templateName?: string | null;
  toEmailList?: string[];
  fromEmailList: string[];
  resizable?: boolean;
  fromEmailSignature?: string;
  disableSend?: boolean;
  disableEdit?: boolean;
  updateFromEmailSignature?: (fEmail: string) => void;
  replaceEmailHandler?: (oldEmail: string, newEmail: string) => void;
  manageSenderAccounts: () => void;
  changeJob: () => void;
  openTemplatesList: () => void;
  removeSelectedTemplate: () => void;
  uploadAttachment: (arg1: File) => Promise<void>;
  handleAttachmentError: () => void;
  submitAction: <T>(arg: T) => Promise<void>;
  handleClose: () => void;
  deleteAttachmentHandler?: (selectedFileName: string) => void;
  deleteAllAttachments?: () => void;
  ccList?: string[];
  bccList?: string[];
  validateEmails?: (
    toEmail: string[],
    ccEmails?: string[],
    bccEmails?: string[]
  ) => Promise<[invalidToEmails: string[], invalidCcEmails: string[], invalidBccEmails: string[]]>;
  isJobPosterEmail?: boolean;
  defaultAttachmentFileNames?: (string | null)[];
  showImportTemplates?: boolean;
  footerSize?: string;
  setBody?: (body: string, to: string[], cc: string[], bcc: string[]) => void;
  isLoaded?: boolean;
  fromSubmissions?: boolean;
}

const BulkEmailForm = ({
  body,
  jobTitle,
  jobCode,
  fromEmailList = [],
  toEmailList = [],
  template = '',
  subjectTemplate = '',
  templateName,
  resizable = true,
  fromEmailSignature = '',
  disableSend = false,
  updateFromEmailSignature,
  manageSenderAccounts,
  changeJob,
  openTemplatesList,
  removeSelectedTemplate,
  uploadAttachment,
  handleAttachmentError,
  submitAction,
  handleClose,
  validateEmails,
  deleteAttachmentHandler,
  deleteAllAttachments,
  ccList = [],
  bccList = [],
  isJobPosterEmail = false,
  defaultAttachmentFileNames = [],
  showImportTemplates = true,
  footerSize = 'medium',
  setBody,
  disableEdit,
  isLoaded = true,
  fromSubmissions = false,
}: IBulkEmailFormProps) => {
  const noEmail = isEmpty(toEmailList[0]);
  if (!isJobPosterEmail) {
    validationSchema.to = array()
      .of(string().email('Please enter valid email addresses'))
      .min(1, 'Please enter at least one email address');
  }
  const [openInvalidWarning, setOpenInvalidWarning] = useState(false);
  const [invalidEmailData, setInvalidEmails] = useState<{
    invalidToEmails: string[];
    invalidBCCEmails: string[];
    invalidCCEmails: string[];
  }>({
    invalidToEmails: [],
    invalidBCCEmails: [],
    invalidCCEmails: [],
  });
  const EmailDefaultValues = {
    to: [],
    cc: [],
    bcc: [],
    from: fromEmailList[0],
    subject: '',
    body: '',
  };

  const formOptions = {
    resolver: yupResolver(object().shape(validationSchema)),
    defaultValues: EmailDefaultValues,
  };
  const methods = useForm(formOptions);
  const { getValues, setValue } = methods;

  const [openCC, setOpenCC] = useState<boolean>(false);

  const [openBCC, setOpenBCC] = useState<boolean>(false);

  const [validateEmail, setValidateEmail] = useState(false);

  const [fileNames, setFileNames] = useState<(string | null)[]>(defaultAttachmentFileNames);

  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);

  useEffect(() => {
    if (defaultAttachmentFileNames) {
      setFileNames(defaultAttachmentFileNames);
    }
  }, [defaultAttachmentFileNames]);

  const handleAttachments = (ev: ChangeEvent<HTMLInputElement>) => {
    if (ev.target.files && ev.target.files[0].size < 25000000) {
      ev.target.files && uploadAttachment(ev.target.files[0]);
      setFileNames((prev) => [...prev, ev.target.files && ev.target.files[0].name]);
    } else handleAttachmentError();
  };
  const onDeleteAttachment = (selectedFileName: string) => {
    const tempData: (string | null)[] = fileNames.filter((name) => selectedFileName !== name);
    setFileNames([...tempData]);
    deleteAttachmentHandler && deleteAttachmentHandler(selectedFileName);
  };

  const removeAllAttachments = () => {
    deleteAllAttachments && deleteAllAttachments();
    setFileNames([]);
  };

  const editor = useWsEditor('', (htmlBody) => {
    methods.setValue('body', htmlBody);
    setBody &&
      htmlBody &&
      setBody(htmlBody, methods.getValues('to'), methods.getValues('cc'), methods.getValues('bcc'));
  });

  const deleteTemplate = () => {
    editor?.commands.setContent('');
    methods.setValue('body', '');
    methods.setValue('subject', '');
    removeSelectedTemplate();
  };

  const [showAllAttachments, setShowAllAttachments] = useState<boolean>(false);
  useEffect(() => {
    const bodyData = `<p> </p><p> </p><p> </p>${
      body ? JSON.parse(body).replace(/\n/g, '<br />') : template
    }  </tbody></table><p> </p><p> </p><p> </p>`;
    editor?.commands.setContent(bodyData, false);
    methods.setValue('body', bodyData);
  }, [methods, editor, template, fromEmailSignature, body]);

  useEffect(() => {
    methods.setValue('subject', subjectTemplate);
  }, [methods, subjectTemplate]);

  useEffect(() => {
    methods.setValue('cc', ccList);
    setOpenCC(ccList?.length > 0);
  }, [methods, ccList]);

  useEffect(() => {
    methods.setValue('bcc', bccList);
    setOpenBCC(bccList?.length > 0);
  }, [methods, bccList]);

  useEffect(() => {
    const validate = async () => {
      let validEmailData: [string[], string[], string[]] = [[], [], []];
      if (!isJobPosterEmail && validateEmails) {
        validEmailData = await validateEmails(toEmailList, ccList, bccList);
      }
      const [invalidToEmails, invalidCCEmails, invalidBCCEmails] = validEmailData;
      const validToEmails = toEmailList.filter((email) => invalidToEmails.indexOf(email) == -1);
      const validCCEmails = ccList.filter((email) => invalidCCEmails.indexOf(email) == -1);
      const validBCCEmails = bccList.filter((email) => invalidBCCEmails.indexOf(email) == -1);
      methods.setValue('to', validToEmails);
      methods.setValue('cc', validCCEmails);
      methods.setValue('bcc', validBCCEmails);
      setOpenCC(ccList?.length > 0);
      setOpenBCC(bccList?.length > 0);
      setInvalidEmails({
        invalidToEmails: invalidToEmails,
        invalidBCCEmails: invalidBCCEmails,
        invalidCCEmails: invalidCCEmails,
      });
      setValidateEmail(true);
      (invalidToEmails.length > 0 || invalidBCCEmails.length > 0 || invalidCCEmails.length > 0) &&
        setOpenInvalidWarning(true);
    };
    if (validateEmails && !validateEmail) validate();
  }, [bccList, ccList, isJobPosterEmail, methods, toEmailList, validateEmail, validateEmails]);

  const handlePreview = () => {
    setPreviewDialogOpen(true);
  };

  const handlePreviewProceed = (updatedBody?: string) => {
    if (updatedBody) {
      setValue('body', updatedBody);
      editor?.commands.setContent(updatedBody);
      setBody && setBody(updatedBody, getValues('to'), getValues('cc'), getValues('bcc'));
    }
    setPreviewDialogOpen(false);
  };

  const handleSetEmailBodyFromPreview = (body: string) => {
    setValue('body', body);
    editor?.commands.setContent(body);
  };

  const handleTemplateRefreshInPreview = () => {
    const bodyData = template;
    setValue('body', bodyData);
    editor?.commands.setContent(bodyData);
  };

  const SenderInput = () => (
    <Grid xs={6}>
      <Controller
        name="from"
        control={methods.control}
        render={({ field }) => (
          <Select
            fullWidth
            value={field.value}
            onChange={(value) => {
              field.onChange(value);
              updateFromEmailSignature && updateFromEmailSignature(value.target.value);
            }}
            defaultValue={fromEmailList[0]}
            variant="standard"
            disableUnderline
            label={''}
          >
            {fromEmailList.map((email, i) => (
              <MenuItem value={email} key={i}>
                {email}
              </MenuItem>
            ))}
          </Select>
        )}
      />
    </Grid>
  );

  const FormAttachment = () => (
    <FileInput htmlFor="attachments">
      <input
        name="attachments"
        id="attachments"
        type="file"
        style={{ display: 'none' }}
        onChange={(ev) => handleAttachments(ev)}
      />
      <AttachmentIcon />
      <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.PRIMARY} cursor="pointer">
        Attach File
      </WsTypography>
    </FileInput>
  );

  return (
    <Layout>
      <Grid item xs={12}>
        <FormProvider {...methods}>
          <Box
            component="form"
            onSubmit={methods.handleSubmit((data) => {
              data.body = `${data.body}\n\n\n ${fromEmailSignature}`;
              submitAction(data);
            })}
          >
            <Grid container sx={{ height: '80vh', overflowY: 'auto' }}>
              <Grid item xs={12}>
                {!disableEdit && (
                  <EmailTextBorder>
                    <Stack direction="row" alignItems={'center'}>
                      {FormLabel('From')}
                      {SenderInput()}
                      <Grid item xs={4}>
                        <Stack direction="row" justifyContent={'flex-end'}>
                          <WsButton
                            variant="text"
                            startIcon={<BorderColorOutlinedIcon />}
                            onClick={manageSenderAccounts}
                          >
                            Add/Manage Accounts
                          </WsButton>
                        </Stack>
                      </Grid>
                    </Stack>
                  </EmailTextBorder>
                )}
                <EmailTextBorder>
                  <Stack direction="row" alignItems={'center'}>
                    {FormLabel('To')}
                    {ReceiverInput('to', disableEdit)}
                    <Grid item xs={3}>
                      <Stack spacing={2} direction="row" justifyContent={'flex-end'}>
                        <WsButton
                          disabled={disableEdit}
                          variant="text"
                          onClick={() => setOpenCC(!openCC)}
                        >
                          Cc
                        </WsButton>
                        <WsButton
                          disabled={disableEdit}
                          variant="text"
                          onClick={() => setOpenBCC(!openBCC)}
                        >
                          Bcc
                        </WsButton>
                      </Stack>
                    </Grid>
                  </Stack>
                </EmailTextBorder>
                {openCC && (
                  <EmailTextBorder>
                    <Stack direction="row" alignItems={'center'}>
                      {FormLabel('Cc')}
                      {ReceiverInput('cc')}
                      <Grid item xs={3}>
                        <Stack spacing={2} direction="row" justifyContent={'flex-end'}>
                          <WsButton type="button" variant="text" onClick={() => setOpenCC(false)}>
                            Close Cc
                          </WsButton>
                        </Stack>
                      </Grid>
                    </Stack>
                  </EmailTextBorder>
                )}
                {openBCC && (
                  <EmailTextBorder>
                    <Stack direction="row" alignItems={'center'}>
                      {FormLabel('Bcc')}
                      {ReceiverInput('bcc')}
                      <Grid item xs={3}>
                        <Stack spacing={2} direction="row" justifyContent={'flex-end'}>
                          <WsButton type="button" variant="text" onClick={() => setOpenBCC(false)}>
                            Close BCC
                          </WsButton>
                        </Stack>
                      </Grid>
                    </Stack>
                  </EmailTextBorder>
                )}
                {jobTitle !== null && (
                  <EmailTextBorder>
                    <Stack direction="row" alignItems={'center'}>
                      {FormLabel('Job')}
                      {JobInput(jobCode, jobTitle)}
                      <Grid item xs={3}>
                        <Stack spacing={1} direction="row" justifyContent={'flex-end'}>
                          <WsButton
                            size="small"
                            variant="text"
                            startIcon={<BorderColorOutlinedIcon fontSize="small" />}
                            onClick={changeJob}
                          >
                            Change
                          </WsButton>
                        </Stack>
                      </Grid>
                    </Stack>
                  </EmailTextBorder>
                )}
                <EmailTextBorder>
                  <Stack direction="row" alignItems={'center'}>
                    {FormLabel('Subject')}
                    {SubjectInput(disableEdit)}
                  </Stack>
                </EmailTextBorder>
                <WsEditor
                  disable={disableEdit}
                  editor={editor}
                  templateName={templateName ?? ''}
                  resizable={resizable}
                  handleChipClick={openTemplatesList}
                  handleChipDelete={deleteTemplate}
                  editorHeight={400}
                  isLoaded={isLoaded}
                />
                <div
                  dangerouslySetInnerHTML={{
                    __html: `<div style="margin:20px;">${fromEmailSignature}</div>`,
                  }}
                ></div>
                {methods.formState.errors.body && (
                  <WsTypography color={WsTypoColors.ERROR}>The body is empty!</WsTypography>
                )}
              </Grid>
            </Grid>
            <EmailFooter item xs={12} size={footerSize}>
              {fileNames.length > 0 && (
                <Grid container width={'fit-content'} alignItems="center" p={0.5}>
                  {fileNames.map((fileName: string, key: number) => {
                    if (key > 1 && !showAllAttachments) {
                      return;
                    } else {
                      return (
                        <Chip
                          key={key}
                          label={fileName}
                          onDelete={() => onDeleteAttachment(fileName)}
                        />
                      );
                    }
                  })}
                  {!showAllAttachments && fileNames.length - 2 > 0 && (
                    <WsTypography
                      variant={WsTypoVariants.BODY1}
                      color={WsTypoColors.PRIMARY}
                      onClick={() => setShowAllAttachments(true)}
                      cursor="pointer"
                    >
                      {`+${fileNames.length - 2} More`}
                    </WsTypography>
                  )}
                  {showAllAttachments && (
                    <WsTypography
                      variant={WsTypoVariants.BODY1}
                      color={WsTypoColors.PRIMARY}
                      onClick={() => setShowAllAttachments(false)}
                      cursor="pointer"
                    >
                      Show Less
                    </WsTypography>
                  )}
                </Grid>
              )}
              <Stack direction="row" justifyContent={'space-between'} alignItems="center">
                <Stack direction="row" spacing={2} alignItems="center">
                  {showImportTemplates && (
                    <WsButton
                      disabled={disableEdit}
                      variant="text"
                      startIcon={<EmailTemplateIcon />}
                      onClick={openTemplatesList}
                    >
                      Select Template
                    </WsButton>
                  )}
                  {!disableEdit && FormAttachment()}

                  {fileNames.length > 2 && deleteAllAttachments && (
                    <Grid item>
                      <WsTypography
                        variant={WsTypoVariants.BODY1}
                        color={WsTypoColors.PRIMARY}
                        onClick={removeAllAttachments}
                        cursor="pointer"
                      >
                        Remove All Attachments
                      </WsTypography>
                    </Grid>
                  )}
                </Stack>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <WsButton
                    size="large"
                    variant="outlined"
                    startIcon={<Cancel />}
                    onClick={handleClose}
                  >
                    Cancel
                  </WsButton>
                  {fromSubmissions && (
                    <WsButton size="large" onClick={() => handlePreview()}>
                      Preview
                    </WsButton>
                  )}
                  <WsButton size="large" startIcon={<Send />} disabled={disableSend} type="submit">
                    Send
                  </WsButton>
                </Stack>
              </Stack>
            </EmailFooter>
          </Box>
        </FormProvider>
      </Grid>
      <WsDialog openDialog={previewDialogOpen} size={WsDialogSize.MEDIUM}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          p={1}
          bgcolor={WsColors.BorderGrey}
        >
          <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.BLACK} bold>
            Email Preview
          </WsTypography>
          <IconButton onClick={() => setPreviewDialogOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <DialogContent sx={{ p: 3 }}>
          <EmailPreview
            emailBody={methods.getValues('body')}
            emailSubject={methods.getValues('subject')}
            isLoaded={isLoaded}
            onProceed={handlePreviewProceed}
            setEmailBody={handleSetEmailBodyFromPreview}
            handleTemplateChange={handleTemplateRefreshInPreview}
          />
        </DialogContent>
      </WsDialog>
      <WsDialog openDialog={openInvalidWarning} size={WsDialogSize.SMALL}>
        <>
          <CloseIcon
            sx={{ cursor: 'pointer', marginLeft: '94%', marginTop: '1%', color: WsColors.Red500 }}
            fontSize={'small'}
            onClick={() => setOpenInvalidWarning(false)}
          />
          <Item sx={{ pt: 0 }}>
            <WsTypography variant={WsTypoVariants.SUB_TITLE}>
              {`${
                noEmail
                  ? 'Candidate Email not available, Update candidate email to send Email'
                  : 'We have removed the Invalid Emails listed below from your Email Campaign'
              }`}
            </WsTypography>
            {BuildInvalidEmailData(invalidEmailData.invalidToEmails, 'To Emails')}
            {BuildInvalidEmailData(invalidEmailData.invalidCCEmails, 'CC Emails')}
            {BuildInvalidEmailData(invalidEmailData.invalidBCCEmails, 'BCC Emails')}
          </Item>
        </>
      </WsDialog>
    </Layout>
  );
};

export default BulkEmailForm;

const BuildInvalidEmailData = (emails: string[], label: string) => (
  <>
    {emails.length > 0 && (
      <Box mt={2}>
        <WsTypography variant={WsTypoVariants.CAPTION}>{label}</WsTypography>
        {emails.map((email, index) => {
          if (index != 0) {
            return (
              <WsTypography display="inline" bold={true} key={index}>{`, ${email}`}</WsTypography>
            );
          }
          return (
            <WsTypography display="inline" bold={true} key={index}>
              {email}
            </WsTypography>
          );
        })}
      </Box>
    )}
  </>
);
